import React from 'react';
import {
  donViSearchColumns,
  loSearchColumns,
  QUERY_KEYS,
  vatTuSearchColumns,
  viTriSearchColumns,
  warehouseSearchColumns
} from '@/constants';
import { SearchFieldV2 as SearchField, FormFieldV2 as FormField } from '@/components/custom/erp';
import { BoPhan, DoiTuong, DonVi, KhoHang, Lo, VatTu, ViTri } from '@/types/schemas';
import { Label } from '@/components/ui/label';
import { FormMode } from '@/types/form';

interface DetailTabProps {
  formMode: FormMode;
  // Original props (keeping for backward compatibility)
  selectedDepartment: BoPhan | null;
  setSelectedDepartment: (value: BoPhan | null) => void;
  selectedWarehouse: KhoHang | null;
  setSelectedWarehouse: (value: KhoHang | null) => void;
  selectedObject: DoiTuong | null;
  setSelectedObject: (value: DoiTuong | null) => void;
  selectedProduct: VatTu | null;
  setSelectedProduct: (value: VatTu | null) => void;

  // New props for DetailTab fields
  selectedMaterial: VatTu | null;
  setSelectedMaterial: (value: VatTu | null) => void;
  selectedImportWarehouse: KhoHang | null;
  setSelectedImportWarehouse: (value: KhoHang | null) => void;
  selectedFromPosition: ViTri | null;
  setSelectedFromPosition: (value: ViTri | null) => void;
  selectedToPosition: ViTri | null;
  setSelectedToPosition: (value: ViTri | null) => void;
  selectedLot: Lo | null;
  setSelectedLot: (value: Lo | null) => void;
  selectedUnit: (DonVi & { ma_unit?: string }) | null;
  setSelectedUnit: (value: (DonVi & { ma_unit?: string }) | null) => void;
}

export const DetailTab: React.FC<DetailTabProps> = ({
  formMode,
  selectedMaterial,
  setSelectedMaterial,
  selectedImportWarehouse,
  setSelectedImportWarehouse,
  selectedFromPosition,
  setSelectedFromPosition,
  selectedToPosition,
  setSelectedToPosition,
  selectedLot,
  setSelectedLot,
  selectedUnit,
  setSelectedUnit
}) => {
  return (
    <div className='space-y-1 p-4'>
      {/* Các trường tìm kiếm (phần trên) */}

      {/* Mã vật tư - Material Search Field */}
      <div className='flex items-center'>
        <Label className='w-40 min-w-40 text-left text-sm font-normal'>Mã vật tư</Label>
        <div className='w-full'>
          <SearchField<VatTu>
            type='text'
            displayRelatedField='ten_vt'
            columnDisplay='ma_vt'
            className='w-[180px]'
            searchEndpoint={`/${QUERY_KEYS.VAT_TU}`}
            searchColumns={vatTuSearchColumns}
            dialogTitle='Danh mục vật tư'
            value={selectedMaterial?.ma_vt || ''}
            onRowSelection={setSelectedMaterial}
            disabled={formMode === 'view'}
          />
        </div>
      </div>

      {/* Mã kho nhập - Import Warehouse Search Field */}
      <div className='flex items-center'>
        <Label className='w-40 min-w-40 text-left text-sm font-normal'>Mã kho nhập</Label>
        <div className='w-full'>
          <SearchField<KhoHang>
            type='text'
            displayRelatedField='ten_kho'
            columnDisplay='ma_kho'
            className='w-[180px]'
            searchEndpoint={`/${QUERY_KEYS.KHO_HANG}`}
            searchColumns={warehouseSearchColumns}
            dialogTitle='Danh mục kho hàng'
            value={selectedImportWarehouse?.ma_kho || ''}
            onRowSelection={setSelectedImportWarehouse}
            disabled={formMode === 'view'}
          />
        </div>
      </div>

      {/* Từ vị trí - From Position Search Field */}
      <div className='flex items-center'>
        <Label className='w-40 min-w-40 text-left text-sm font-normal'>Từ vị trí</Label>
        <div className='w-full'>
          <SearchField<ViTri>
            type='text'
            displayRelatedField='ten_vi_tri'
            columnDisplay='ma_vi_tri'
            className='w-[180px]'
            searchEndpoint={`/${QUERY_KEYS.VI_TRI}`}
            searchColumns={viTriSearchColumns}
            dialogTitle='Danh mục vị trí'
            value={selectedFromPosition?.ma_vi_tri || ''}
            onRowSelection={setSelectedFromPosition}
            disabled={formMode === 'view'}
          />
        </div>
      </div>

      {/* Đến vị trí - To Position Search Field */}
      <div className='flex items-center'>
        <Label className='w-40 min-w-40 text-left text-sm font-normal'>Đến vị trí</Label>
        <div className='w-full'>
          <SearchField<ViTri>
            type='text'
            displayRelatedField='ten_vi_tri'
            columnDisplay='ma_vi_tri'
            className='w-[180px]'
            searchEndpoint={`/${QUERY_KEYS.VI_TRI}`}
            searchColumns={viTriSearchColumns}
            dialogTitle='Danh mục vị trí'
            value={selectedToPosition?.ma_vi_tri || ''}
            onRowSelection={setSelectedToPosition}
            disabled={formMode === 'view'}
          />
        </div>
      </div>

      {/* Mã lô - Lot Search Field */}
      <div className='flex items-center'>
        <Label className='w-40 min-w-40 text-left text-sm font-normal'>Mã lô</Label>
        <div className='w-full'>
          <SearchField<Lo>
            type='text'
            displayRelatedField='ten_lo'
            columnDisplay='ma_lo'
            className='w-[180px]'
            searchEndpoint={`/${QUERY_KEYS.LO}`}
            searchColumns={loSearchColumns}
            dialogTitle='Danh mục lô'
            value={selectedLot?.ma_lo || ''}
            onRowSelection={setSelectedLot}
            disabled={formMode === 'view'}
          />
        </div>
      </div>

      {/* Divider line */}
      <div className='my-3 border-t border-gray-200'></div>

      {/* Các trường lọc (phần dưới) */}

      {/* Đơn vị - Unit Search Field */}
      <div className='flex items-center'>
        <Label className='w-40 min-w-40 text-left text-sm font-normal'>Đơn vị</Label>
        <div className='w-full'>
          <SearchField<DonVi>
            type='text'
            displayRelatedField='ten_don_vi'
            columnDisplay='ma_don_vi'
            className='w-[180px]'
            searchEndpoint={`/${QUERY_KEYS.DON_VI}`}
            searchColumns={donViSearchColumns}
            dialogTitle='Danh mục đơn vị tính'
            value={selectedUnit?.ma_unit || ''}
            onRowSelection={setSelectedUnit}
            disabled={formMode === 'view'}
          />
        </div>
      </div>

      {/* Trạng thái - Status Dropdown */}
      <div className='flex items-center'>
        <Label className='w-40 min-w-40 text-left text-sm font-normal'>Trạng thái</Label>
        <div className='flex-1'>
          <FormField
            className='w-[200px]'
            type='select'
            name='trang_thai'
            disabled={formMode === 'view'}
            options={[
              { label: 'Tất cả', value: '' },
              { label: 'Lập chứng từ', value: 'lap_chung_tu' },
              { label: 'Xuất kho', value: 'xuat_kho' }
            ]}
          />
        </div>
      </div>

      {/* Lọc theo người sử dụng - Filter by User Dropdown */}
      <div className='flex items-center'>
        <Label className='w-40 min-w-40 text-left text-sm font-normal'>Lọc theo người sd</Label>
        <div className='flex-1'>
          <FormField
            className='w-[200px]'
            type='select'
            name='loc_theo_nguoi_sd'
            disabled={formMode === 'view'}
            options={[
              { label: 'Tất cả', value: '' },
              { label: 'Người tạo', value: 'nguoi_tao' },
              { label: 'Người duyệt', value: 'nguoi_duyet' }
            ]}
          />
        </div>
      </div>
    </div>
  );
};
